import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, FormControlLabel, Switch, Alert, Grid,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Key as KeyIcon, ListAlt as ListAltIcon,
} from '@mui/icons-material';
import { useTheme, useMediaQuery } from '@mui/material';
import { useCajaBancoStore } from '../store/cajaBancoStore';
import { initialCajaBanco, TIPOS_CAJA_BANCO } from '../types/cajaBanco';
import type { CajaBanco, CajaBancoFormErrors } from '../types/cajaBanco';

const CajasBancosPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    cajasBancos, loading, error, selectedCajaBanco, dialogOpen,
    fetchCajasBancos, saveCajaBanco, deleteCajaBanco,
    setSelectedCajaBanco, openDialog, closeDialog, clearError,
    openPermisosDialog, openTalonariosDialog,
  } = useCajaBancoStore();

  const [formData, setFormData] = useState<CajaBanco>(initialCajaBanco);
  const [errors, setErrors] = useState<CajaBancoFormErrors>({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  useEffect(() => {
    fetchCajasBancos();
  }, [fetchCajasBancos]);

  useEffect(() => {
    if (dialogOpen) {
      setFormData(selectedCajaBanco ? { ...selectedCajaBanco } : { ...initialCajaBanco });
      setErrors({});
    }
  }, [dialogOpen, selectedCajaBanco]);

  const handleChange = (field: keyof CajaBanco, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof CajaBancoFormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: CajaBancoFormErrors = {};
    if (!formData.codigo?.trim()) newErrors.codigo = 'El codigo es requerido';
    if (!formData.nombre?.trim()) newErrors.nombre = 'El nombre es requerido';
    if (!formData.idMoneda) newErrors.idMoneda = 'La moneda es requerida';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      await saveCajaBanco(formData);
    } catch {
      /* handled by store */
    }
  };

  const handleEdit = (cajaBanco: CajaBanco) => {
    setSelectedCajaBanco(cajaBanco);
    openDialog();
  };

  const handleCreate = () => {
    setSelectedCajaBanco(null);
    openDialog();
  };

  const handleDeleteConfirm = async () => {
    if (deleteId) {
      try {
        await deleteCajaBanco(deleteId);
      } catch {
        /* handled by store */
      }
    }
    setDeleteConfirmOpen(false);
    setDeleteId(null);
  };

  const handlePermisos = (cajaBanco: CajaBanco) => {
    setSelectedCajaBanco(cajaBanco);
    openPermisosDialog();
  };

  const handleTalonarios = (cajaBanco: CajaBanco) => {
    setSelectedCajaBanco(cajaBanco);
    openTalonariosDialog();
  };

  const columns: GridColDef[] = [
    { field: 'codigo', headerName: 'Codigo', width: 100 },
    { field: 'nombre', headerName: 'Nombre', flex: 1, minWidth: 200 },
    {
      field: 'tipoCajaBanco',
      headerName: 'Tipo',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'BANCO' ? 'primary' : 'secondary'}
          variant="outlined"
        />
      ),
    },
    { field: 'monedaSimbolo', headerName: 'Moneda', width: 80 },
    { field: 'numeroCuentaBancaria', headerName: 'Nro. Cuenta', width: 180 },
    { field: 'cuentaContable', headerName: 'Cta. Contable', width: 150 },
    {
      field: 'activo',
      headerName: 'Estado',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Activo' : 'Inactivo'}
          size="small"
          color={params.value ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => handleEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Permisos">
            <IconButton size="small" onClick={() => handlePermisos(params.row)}>
              <KeyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {params.row.tipoCajaBanco === 'BANCO' && (
            <Tooltip title="Talonarios">
              <IconButton size="small" onClick={() => handleTalonarios(params.row)}>
                <ListAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setDeleteId(params.row.idCajaBanco);
                setDeleteConfirmOpen(true);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Cajas y Bancos</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          {isMobile ? 'Nuevo' : 'Nueva Caja/Banco'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ height: 'calc(100vh - 200px)' }}>
        <DataGrid
          rows={cajasBancos}
          columns={columns}
          getRowId={(row) => row.idCajaBanco}
          loading={loading}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>{selectedCajaBanco ? 'Editar Caja/Banco' : 'Nueva Caja/Banco'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Codigo"
                fullWidth
                required
                value={formData.codigo}
                onChange={(e) => handleChange('codigo', e.target.value)}
                error={!!errors.codigo}
                helperText={errors.codigo}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                label="Nombre"
                fullWidth
                required
                value={formData.nombre}
                onChange={(e) => handleChange('nombre', e.target.value)}
                error={!!errors.nombre}
                helperText={errors.nombre}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Tipo"
                fullWidth
                select
                value={formData.tipoCajaBanco}
                onChange={(e) => handleChange('tipoCajaBanco', e.target.value)}
              >
                {TIPOS_CAJA_BANCO.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Moneda (ID)"
                fullWidth
                required
                type="number"
                value={formData.idMoneda || ''}
                onChange={(e) => handleChange('idMoneda', parseInt(e.target.value) || 0)}
                error={!!errors.idMoneda}
                helperText={errors.idMoneda}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Nro. Cuenta Bancaria"
                fullWidth
                value={formData.numeroCuentaBancaria || ''}
                onChange={(e) => handleChange('numeroCuentaBancaria', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Cuenta Contable"
                fullWidth
                value={formData.cuentaContable || ''}
                onChange={(e) => handleChange('cuentaContable', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.requiereNumeroOperacion}
                    onChange={(e) => handleChange('requiereNumeroOperacion', e.target.checked)}
                  />
                }
                label="Req. Nro. Operacion"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.activo}
                    onChange={(e) => handleChange('activo', e.target.checked)}
                  />
                }
                label="Activo"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirmar Eliminacion</DialogTitle>
        <DialogContent>
          <Typography>Esta seguro de eliminar esta caja/banco?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CajasBancosPage;
